/* eslint-disable no-underscore-dangle */
// @flow

import React, { useState, useEffect } from 'react';
import { useSSR, I18nextProvider } from 'react-i18next';
import i18next from 'i18next';

import { ThemeProvider } from 'styled-components';
import { Provider } from 'react-redux';

import { compose } from 'lodash/fp';

import withRedux from 'next-redux-wrapper';
import withReduxSaga from 'next-redux-saga';

import { config } from '@fortawesome/fontawesome-svg-core';

import moment from 'moment-timezone';
import 'moment/locale/de';

import App from 'next/app';
import Head from 'next/head';

import { GlobalStyle } from '@styles/global';

import store from '@redux/store';
import { Organization } from '@components/SchemaDefinitions';

import { needsToFetchData } from '@redux/selectors';
import { requestPersonalData } from '@screens/Profile/redux/actions';

import { INDEX } from '@routes';
import { nextRoutes, Router } from '@i18n';
import Notification from '@components/Notification/index';
import { setCurefyDomain } from '@redux/components/Page/actions';

import '@lib/muteWarnings';

import type { GlobalState } from '@globalTypes';

import { authorizationService, cookieService } from '@services';

import theme from '@theme';

import {
  NavigationMain,
  Footer,
  // CookieConsent,
  ConfirmationDialog,
  CanonicalLink,
  Modal
} from '@components';

config.autoAddCss = false;

const styledTheme = {
  breakpoints: theme.breakpoints,
  spacing: theme.spacing
};

class MedimakrWeb extends App {
  static async getInitialProps({
    Component, ctx, ctx: { req, res, store }
  }) {
    const isServer = req || res;

    // When req.i18n is undefined this means a client side routing event (Link/Router component)
    // In this case we use i18next which was initialized on the intial client side render
    const i18n = req?.i18n || i18next;
    // Remove toJSON so server side serialization attempt does not fail.
    i18n.toJSON = () => null;

    // There are cases where the locale is in the wrong format
    // We need to handle these cases to ensure proper localization of routes and text

    const currentCountry = 'IN';
    const locale = ((): string => {
      const maybeLocale: ?string = req?.locale || nextRoutes.locale;
      if (maybeLocale) return maybeLocale;

      return 'en';
    })();

    const currentLanguage = locale;

    const initialI18nStore = i18n.store?.data;

    const accessToken = cookieService.getAccessToken(req);


    const subject = { accessToken };
    const predicate = Component.isAuthorized;
    const redirect = isServer ? `/${currentLanguage}` : INDEX;

    if (!authorizationService.checkIsAuthorized(predicate, subject, redirect, res)) {
      return {};
    }
    const { dispatch, getState } = store;
    const { page } = getState();
    const { curefyDomain } = page;

    if (curefyDomain === undefined) {
      await dispatch(setCurefyDomain(req));
    }

    moment.locale(currentLanguage);

    const pageProps = Component.getInitialProps ? await Component.getInitialProps(ctx) : {};

    return {
      pageProps,
      isServer: ctx.isServer,
      pathname: req?.url,
      currentLanguage,
      currentCountry,
      locale,
      query: ctx.query,
      i18n,
      initialI18nStore,
      namespaces: i18n.options.ns,
      accessToken,
    };
  }

  fetchPersonalData = () => {
    const { store } = this.props;
    const state: GlobalState = store.getState();
    const shouldFetchData: boolean = needsToFetchData(state.personalData);

    if (shouldFetchData) store.dispatch(requestPersonalData());
  };

  componentDidMount(): void {
    const { accessToken } = this.props;

    if (accessToken) {
      this.fetchPersonalData();
    }
  }

  render() {
    const {
      Component,
      pageProps,
      store,
      isServer,
      pathname,
      currentLanguage,
      query,
      locale,
      i18n,
      initialI18nStore,
      namespaces,
      accessToken,
      currentCountry
    } = this.props;

    nextRoutes.setLocale(locale);

    if (!i18n) {
      // When rendering on the client the server i18n is not available since
      // the instance can not be serialized and rehydrated
      // So we have to init one on the client side
      // It does not need a backend since the store will be filled from the server side rendered one
      i18next.init({
        initImmediate: true,
        debug: false,
        load: 'currentOnly',
        preload: ['en', 'ar'],
        fallbackLng: 'en',
        ns: namespaces,
        defaultNS: 'common',
        whitelist: ['en', 'ar'],
        react: {
          useSuspense: false
        },
        detection: {
          order: []
        },
        interpolation: {
          escapeValue: false // not needed for react as it escapes by default
        }
      });
    }
    nextRoutes.setLocale(locale);

    moment.locale(currentLanguage);

    return (
      <I18nextProvider i18n={i18n || i18next}>
        <MedimakrApp
          pathname={pathname || Router.asPath}
          isServer={isServer}
          initialLanguage={currentLanguage}
          initialCountry={currentCountry}
          initialI18nStore={initialI18nStore}
          component={Component}
          pageProps={pageProps}
          query={query}
          store={store}
          accessToken={accessToken}
        />
      </I18nextProvider>
    );
  }
}


const MedimakrApp = React.memo(
  ({
    initialLanguage,
    initialCountry,
    initialI18nStore,
    component,
    pageProps,
    query,
    store,
    accessToken,
    pathname,
  }) => {
    useSSR(initialI18nStore, initialLanguage);

    const Component = component;
    const [modalOpen, setModalOpen] = useState(false);
    const [signUp, setSignUp] = useState(false);

    // eslint-disable-next-line no-unused-vars
    const [flowModal, setFlowModal] = useState(false);

    const { getState } = store;
    const { page } = getState();
    const { curefyDomain } = page;

    useEffect(() => {
      const pathsToExclude = ['opinion-patient-flow', 'profile/', 'consultation-patient-flow', 'opinion-flow', 'consultation-flow', 'treatment-cost-flow', 'treatment-plan-flow'];
      const excludePage = pathsToExclude.some(path => window.location.pathname.includes(path));
      if (!excludePage) {
        const script = document.createElement('script');
        script.async = true;
        script.src = curefyDomain
          ? 'https://embed.tawk.to/6542970ff2439e1631eac074/1he61s5sd'
          : 'https://embed.tawk.to/6486e89394cf5d49dc5d26fa/1h2nfp0pu';

        // document.head.appendChild(s);
        if (document.head) {
          document.head.appendChild(script);
        }

        return () => {
        // document.head.removeChild(s);
          if (script && script.parentNode) {
            script.parentNode.removeChild(script);
          }
        };
      }
      return undefined;
    }, []);

    if (typeof document !== 'undefined') {
      const shadowRoot: any = document.getElementById('__next-build-watcher');
      const container = shadowRoot ? shadowRoot.shadowRoot.getElementById('container') : null;
      const IconWrapper = container ? container.children[0] : null;
      const Svg = IconWrapper ? IconWrapper.children[0] : null;

      const Loader = document.createElement('img');
      Loader.src = 'https://yapita-production.s3.ap-south-1.amazonaws.com/images/cropedlogo.gif';
      Loader.style.boxShadow = '0 0 26px rgba(0, 0, 0, 0.5)';
      Loader.style.width = '100px';
      Loader.style.height = '100px';
      Loader.style.borderRadius = '20px';

      if (Svg && IconWrapper) {
        IconWrapper.removeChild(Svg);
        IconWrapper.appendChild(Loader);
        IconWrapper.style.width = 'unset';
        IconWrapper.style.height = 'unset';
      }

      if (container) {
        container.style.width = '100%';
        container.style.height = '100%';
        container.style.pointerEvents = 'all';
        container.style.justifyContent = 'center';
        container.style.background = 'rgb(0, 0, 0, 0.4)';
        container.style.padding = '0px';
        container.style.bottom = '0px';
        container.style.right = '0px';
      }
    }


    return (
      <>
        {
          !curefyDomain
            ? (
              <Head>
                <meta content={`${initialLanguage}_${initialCountry}`} property="og:locale" />
                <meta content="index, follow" name="robots" key="robots" />
                <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
                <link rel="icon" type="image/png" sizes="32x32" href="/surgery/wp-content/uploads/fbrfg/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="16x16" href="/surgery/wp-content/uploads/fbrfg/favicon-16x16.png" />
                <link rel="manifest" href="/surgery/wp-content/uploads/fbrfg/site.webmanifest" />
                <link rel="mask-icon" href="/static/images/logo-white.svg" color="#5bbad5" />
                <link rel="shortcut icon" href="/favicon.ico" />
                <link rel="icon" href="/favicon.ico" />
                <meta name="msapplication-TileColor" content="#da532c" />
                <meta name="msapplication-config" content="/surgery/wp-content/uploads/fbrfg/browserconfig.xml" />
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                      '@context': 'https://schema.org',
                      '@type': 'Organization',
                      name: 'Yapita Health',
                      url: 'https://yapitahealth.com',
                      aggregateRating: {
                        '@type': 'AggregateRating',
                        ratingValue: '4.9',
                        ratingCount: 1286
                      }
                    })
                  }}
                />
              </Head>
            )
            : (
              <Head>
                <meta content={`${initialLanguage}_${initialCountry}`} property="og:locale" />
                <meta content="index, follow" name="robots" key="robots" />
                <link rel="apple-touch-icon" sizes="180x180" href="/gp/wp-content/uploads/fbrfg/apple-touch-icon.png" />
                <link rel="icon" type="image/png" sizes="32x32" href="/gp/wp-content/uploads/fbrfg/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="16x16" href="/gp/wp-content/uploads/fbrfg/favicon-16x16.png" />
                <link rel="manifest" href="/gp/wp-content/uploads/fbrfg/site.webmanifest" />
                <link rel="mask-icon" href="/static/images/svg/curefy-logo.svg" color="#5bbad5" />
                <meta name="msapplication-TileColor" content="#2bcaa6" />
                <link rel="shortcut icon" href="/images/curefy-favicon.ico" />
                <link rel="icon" href="/images/curefy-favicon.ico" />
                <meta name="msapplication-config" content="/gp/wp-content/uploads/fbrfg/browserconfig.xml" />
              </Head>
            )
          }
        <Notification />

        <CanonicalLink query={query} pathname={pathname} />

        <GlobalStyle />
        <Provider store={store}>
          <ThemeProvider theme={styledTheme}>
            <>
              <Organization />
              <NavigationMain accessToken={accessToken} setModalOpen={setModalOpen} />
              <Component
                {...{
                  ...pageProps,
                  query,
                  setModalOpen,
                  setSignUp,
                  setFlowModal
                }}
              />
              <Modal modalState={modalOpen} handleOpen={setModalOpen} signUp={signUp} />
              <Footer />
              {/* <CookieConsent /> */}
              <ConfirmationDialog />
            </>
          </ThemeProvider>
        </Provider>
      </>
    );
  }
);

const MedimakrWebConnected = compose(
  withRedux(store),
  withReduxSaga
)(MedimakrWeb);

export default MedimakrWebConnected;
